import React from "react";
import { FaPlus, FaMinus } from "react-icons/fa";
import { CircularButton } from "./CircularButton";

interface SubdivisionsSphereProps {
  subdivision: number;
  onSubdivisionChange: (subdivision: number) => void;
}

// Function to get subdivision display information
const getSubdivisionDisplay = (subdivision: number) => {
  switch (subdivision) {
    case 1:
      return { name: "None", symbol: "-" };
    case 2:
      return { name: "8th Notes", symbol: "2" };
    case 3:
      return { name: "Triplets", symbol: "3" };
    case 4:
      return { name: "Quarters", symbol: "4" };
    case 5:
      return { name: "Quintuplets", symbol: "5" };
    case 6:
      return { name: "Sextuplets", symbol: "6" };
    case 7:
      return { name: "Septuplets", symbol: "7" };
    default:
      return { name: "None", symbol: "-" };
  }
};

export const SubdivisionsSphere: React.FC<SubdivisionsSphereProps> = ({
  subdivision,
  onSubdivisionChange,
}) => {
  const handleDecrease = () => {
    if (subdivision === 7) {
      onSubdivisionChange(6); // 7 to 6
    } else if (subdivision === 6) {
      onSubdivisionChange(5); // 6 to 5
    } else if (subdivision === 5) {
      onSubdivisionChange(4); // 5 to 4
    } else if (subdivision === 4) {
      onSubdivisionChange(3); // 4 to 3
    } else if (subdivision === 3) {
      onSubdivisionChange(2); // 3 to 8th notes
    } else if (subdivision === 2) {
      onSubdivisionChange(1); // 8th notes to none
    }
  };

  const handleIncrease = () => {
    if (subdivision === 1) {
      onSubdivisionChange(2); // none to 8th notes
    } else if (subdivision === 2) {
      onSubdivisionChange(3); // 8th notes to triplets
    } else if (subdivision === 3) {
      onSubdivisionChange(4); // 3 to 4 (quarters)
    } else if (subdivision === 4) {
      onSubdivisionChange(5); // 4 to 5
    } else if (subdivision === 5) {
      onSubdivisionChange(6); // 5 to 6
    } else if (subdivision === 6) {
      onSubdivisionChange(7); // 6 to 7
    }
  };

  const displayInfo = getSubdivisionDisplay(subdivision);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "20px",
      }}
    >
      {/* Left button - Decrease subdivision */}
      <CircularButton
        onClick={handleDecrease}
        disabled={subdivision <= 1}
        size={44}
        ariaLabel="Decrease subdivision"
        tooltip="Decrease subdivision"
        tooltipPosition="top"
      >
        {FaMinus({ size: 14 })}
      </CircularButton>

      {/* Center - Subdivisions Sphere */}
      <div
        className="subdivisions-sphere"
        style={{
          borderRadius: "50%",
          background: "var(--overlay-bg)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          backdropFilter: "blur(7px)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          transition: "all 0.15s ease-out",
          width: 150,
          height: 150,
        }}
      >
        <div
          className="subdivisions-sphere-text"
          style={{
            fontWeight: 600,
            color: "var(--text-primary)",
            textAlign: "center",
            userSelect: "none",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            position: "relative",
          }}
        >
          {/* Label */}
          <div
            style={{
              color: "var(--text-secondary)",
              userSelect: "none",
              position: "absolute",
              top: "64%",
              left: "50%",
              transform: "translateX(-50%)",
              whiteSpace: "nowrap",
              fontSize: 12,
              letterSpacing: 0.2,
            }}
          >
            Subdivision
          </div>

          {/* Subdivision value display */}
          <div
            style={{
              fontWeight: 700,
              color: "var(--text-primary)",
              lineHeight: 1,
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              fontSize: 30,
              letterSpacing: 0.3,
            }}
          >
            {displayInfo.symbol}
          </div>
        </div>
      </div>

      {/* Right button - Increase subdivision */}
      <CircularButton
        onClick={handleIncrease}
        disabled={subdivision >= 7}
        size={44}
        ariaLabel="Increase subdivision"
        tooltip="Increase subdivision"
        tooltipPosition="top"
      >
        {FaPlus({ size: 14 })}
      </CircularButton>
    </div>
  );
};
